import React, { useState, useId, useRef, useCallback } from 'react';
import { useModalFocus } from '@/shared/hooks/useModalFocus';
import { X, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, ButtonVariant } from '@/shared/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { useToast } from '@/shared/contexts/ToastContext';
import { createUsersApi } from '@/features/system/api/usersApi';
import { useRuntimeConfigContext } from '@/shared/providers/RuntimeConfigProvider';
import { MIN_PASSWORD_LENGTH } from '@/features/system/constants/userManagement.constants';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  onChangePassword: (newPassword: string) => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  username,
  onChangePassword,
}) => {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { getConfigValue } = useRuntimeConfigContext();
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleId = useId();
  const passwordHintId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Rails parity: component.passwordPolicy.complexityHint renders as a muted
  // help caption under the password field when the site has configured it.
  const passwordHint =
    (getConfigValue('component.passwordPolicy.complexityHint') as string | undefined)?.trim() || '';

  const handleClose = useCallback(() => {
    setNewPassword('');
    setShowPassword(false);
    onClose();
  }, [onClose]);

  useModalFocus(isOpen, handleClose, dialogRef);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword.trim()) return;

    if (newPassword.trim().length < MIN_PASSWORD_LENGTH) {
      showToast(
        t('system.userManagement.users.validation.passwordTooShort') ||
          `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
        'error'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const usersApi = createUsersApi(apiClient);
      await usersApi.checkPasswordPolicy(newPassword);
    } catch (error) {
      const responseData = (error as any)?.response?.data;
      const errors: { error_specifier?: string; error_class?: string; message?: string }[] =
        responseData?.errors ?? [];
      const isPolicyViolation = errors.some(
        (e) =>
          e.error_specifier === 'illegal_value' &&
          e.error_class === 'validation_error'
      );
      const errorMessage = isPolicyViolation
        ? t('system.userManagement.users.passwordPolicyViolation')
        : responseData?.message ||
          (error as Error)?.message ||
          t('system.userManagement.users.passwordPolicyError') ||
          'Password does not meet policy requirements';
      showToast(errorMessage, 'error');
      setIsSubmitting(false);
      return;
    }

    try {
      await onChangePassword(newPassword);
      setNewPassword('');
      onClose();
    } catch (error) {
      throw new Error("Error changing password");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 z-[999] bg-black bg-opacity-50" aria-hidden="true" />
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="fixed inset-0 z-[999] flex items-center justify-center pointer-events-none"
    >
      <div className="bg-surface rounded-lg shadow-lg w-full max-w-md mx-4 pointer-events-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 id={titleId} className="text-lg font-semibold text-text">
            {t('system.userManagement.users.changePassword') || 'Change password'}
          </h2>
          <button
            onClick={handleClose}
            className="text-text hover:text-text-muted transition-colors"
            aria-label={t('global.close', 'Close')}
            data-testid="close-change-password-modal"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Username */}
          <div className="mb-4">
            <label htmlFor="change-password-username" className="block text-sm font-medium text-text mb-2">
              {t('system.userManagement.users.columns.username') || 'Username'}
            </label>
            <Input
              id="change-password-username"
              type="text"
              value={username}
              autoComplete="username"
              disabled
              className="bg-surface-alt"
              data-testid="username-input"
            />
          </div>

          {/* New Password */}
          <div className="mb-6">
            <label htmlFor="change-password-new" className="block text-sm font-medium text-text mb-2">
              {t('system.userManagement.users.newPassword') || 'New password'} <span className="text-error" aria-hidden="true">*</span>
            </label>
            <div className="relative">
              <Input
                id="change-password-new"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('system.userManagement.users.enterNewPassword') || 'Enter new password'}
                autoComplete="new-password"
                required
                aria-describedby={passwordHint ? passwordHintId : undefined}
                className="pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('global.hidePassword', 'Hide password') : t('global.showPassword', 'Show password')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
              </button>
            </div>
            {passwordHint && (
              <p id={passwordHintId} className="text-text-muted text-xs mt-1">{passwordHint}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant={ButtonVariant.Outline}
              size={ButtonSize.Sm}
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid="cancel-change-password-button"
            >
              {t('global.cancel') || 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant={ButtonVariant.Primary}
              size={ButtonSize.Sm}
              disabled={!newPassword.trim() || isSubmitting}
              data-testid="submit-change-password-button"
            >
              {isSubmitting
                ? (t('common.saving') || 'Saving...')
                : (t('system.userManagement.users.changePassword') || 'Change password')}
            </Button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default ChangePasswordModal;